// src/pages/admin/Production.js
import React, { useState, useEffect, useCallback } from 'react';
import './Production.css';
import { apiCall, getCurrentUser } from '../../utils/api';
import productionIcon from '../../img/production.png';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
const STATUS_OPTIONS = [
  'FINISH', 'WIP', 'YTS', 'RTU', 'PENDING', 'HOLD', 'QUERY', 'REVERT'
];

const QC_STATUS_OPTIONS = [
  'FINISH', 'WIP', 'YTS', 'RTU', 'UPLOADED', 'PENDING', 'HOLD', 'QUERY', 'REVERT'
];

const REF_TYPES = ['BK-REF', 'CH-REF', 'BK/CH-REF', 'FN-REF', 'BK/FN-REF', 'CH/FN-REF', 'PG/FN-REF'];

// ── Bulk Edit Modal (Production) ────────────────────────────────────────────
const PROD_BULK_FIELDS = [
  { key: 'processStatus', label: 'Process Status', type: 'select', options: STATUS_OPTIONS },
  { key: 'qcStatus', label: 'QC Status', type: 'select', options: QC_STATUS_OPTIONS },
  { key: 'endDate', label: 'End Date', type: 'date' },
];

const BulkEditProductionModal = ({ selectedIds, selectedCount, jobs, onClose, onDone }) => {
  const [fields, setFields] = useState(() =>
    Object.fromEntries(PROD_BULK_FIELDS.map(f => [f.key, { enabled: false, value: '' }]))
  );
  const [saving, setSaving] = useState(false);

  const toggleField = key => {
    if (key === 'endDate' && (!fields.qcStatus.enabled || fields.qcStatus.value !== 'UPLOADED')) {
      return;
    }
    setFields(prev => ({ ...prev, [key]: { enabled: !prev[key].enabled, value: prev[key].enabled ? '' : prev[key].value } }));
  };

  const setValue = (key, value) =>
    setFields(prev => {
      const next = { ...prev, [key]: { ...prev[key], value } };
      if (key === 'qcStatus' && value !== 'UPLOADED') {
        next.endDate = { enabled: false, value: '' };
      }
      return next;
    });

  const activeFields = PROD_BULK_FIELDS.filter(f => fields[f.key].enabled);

  const handleApply = async () => {
    if (activeFields.length === 0) { alert('Please enable at least one field to update.'); return; }
    if (activeFields.some(f => !fields[f.key].value)) { alert('Please set a value for every enabled field.'); return; }

    setSaving(true);
    let successCount = 0;
    let failCount = 0;
    try {
      await Promise.all(
        selectedIds.map(async jobId => {
          const job = jobs.find(j => j.id === jobId);
          const payload = {
            processStatus: fields.processStatus.enabled ? fields.processStatus.value : job?.processStatus,
            qcStatus: fields.qcStatus.enabled ? fields.qcStatus.value : job?.qcStatus,
            endDate: fields.endDate.enabled ? fields.endDate.value : job?.endDate,
            employees: job?.employees || [],
          };
          try {
            await apiCall(`/jobs/${jobId}/production`, 'PUT', payload);
            successCount++;
          } catch {
            failCount++;
          }
        })
      );
      if (failCount > 0) {
        alert(`Done: ${successCount} updated, ${failCount} failed.`);
      } else {
        alert(`✅ Successfully updated ${successCount} job${successCount !== 1 ? 's' : ''}.`);
      }
      await onDone();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="prod-bulk-overlay" onClick={onClose}>
      <div className="prod-bulk-modal" onClick={e => e.stopPropagation()}>
        <h3 className="prod-bulk-title">✏️ Bulk Edit — {selectedCount} Job{selectedCount !== 1 ? 's' : ''}</h3>
        <p className="prod-bulk-hint">Toggle the fields you want to update. Only enabled fields will be changed.</p>
        <div className="prod-bulk-fields">
          {PROD_BULK_FIELDS.map(f => {
            const { enabled, value } = fields[f.key];
            const isDisabledField = f.key === 'endDate' && (!fields.qcStatus.enabled || fields.qcStatus.value !== 'UPLOADED');
            return (
              <div key={f.key} className={`prod-bulk-row${enabled ? ' active' : ''}`} style={isDisabledField ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                <label className="prod-bulk-toggle">
                  <input type="checkbox" checked={enabled} disabled={isDisabledField} onChange={() => toggleField(f.key)} />
                  <span className="prod-bulk-label">{f.label}</span>
                </label>
                <div className="prod-bulk-control">
                  {f.type === 'select' ? (
                    <select value={value} disabled={!enabled || isDisabledField} onChange={e => setValue(f.key, e.target.value)}>
                      <option value="">-- Select --</option>
                      {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type="date" value={value} disabled={!enabled || isDisabledField} onChange={e => setValue(f.key, e.target.value)} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="prod-bulk-actions">
          <button className="prod-bulk-cancel" onClick={onClose}>Cancel</button>
          <button
            className="prod-bulk-apply"
            onClick={handleApply}
            disabled={saving || activeFields.length === 0}
          >
            {saving ? 'Applying...' : `Apply to ${selectedCount} Job${selectedCount !== 1 ? '' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Bulk Delete Modal (Production) ─────────────────────────────────
const BulkDeleteProductionModal = ({ selectedCount, onClose, onDelete }) => (
  <div className="prod-bulk-overlay" onClick={onClose}>
    <div className="prod-bulk-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px', textAlign: 'center' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '14px' }}>🗑️</div>
      <h3 className="prod-bulk-title">Bulk Delete</h3>
      <p style={{ fontSize: '0.9rem', color: '#475569', margin: '14px 0 24px' }}>
        Are you sure you want to delete <strong>{selectedCount}</strong> selected jobs?<br />
        This action cannot be undone.
      </p>
      <div className="prod-bulk-actions" style={{ justifyContent: 'center' }}>
        <button className="prod-bulk-cancel" onClick={onClose}>Cancel</button>
        <button
          className="prod-bulk-apply"
          onClick={onDelete}
          style={{ background: '#dc2626' }}
          onMouseEnter={e => e.target.style.background = '#b91c1c'}
          onMouseLeave={e => e.target.style.background = '#dc2626'}
        >
          Delete
        </button>
      </div>
    </div>
  </div>
);

// ── Bulk Reconfirm Delete Modal (Production) ──────────────────────────
const BulkReconfirmDeleteProductionModal = ({ selectedIds, selectedCount, onClose, onDelete }) => {
  const [inputText, setInputText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const isValid = inputText.trim().toUpperCase() === 'DELETE'
    || inputText.trim().toUpperCase() === 'YES';

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(selectedIds);
      onClose();
    } catch (e) {
      alert('Error deleting: ' + e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="prod-bulk-overlay" onClick={onClose}>
      <div className="prod-bulk-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px', textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '14px' }}>⚠️</div>
        <h3 className="prod-bulk-title">Final Confirmation</h3>
        <p style={{ fontSize: '0.9rem', color: '#475569', margin: '14px 0 8px' }}>
          You are about to delete <strong>{selectedCount}</strong> selected jobs.
        </p>
        <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 16px' }}>
          To confirm, type <code style={{ background: '#fee2e2', color: '#b91c1c', padding: '2px 6px', borderRadius: '4px', fontFamily: 'monospace', fontWeight: 'bold' }}>DELETE</code>:
        </p>
        <div style={{ margin: '14px 0' }}>
          <input
            type="text"
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1.5px solid #cbd5e1',
              borderRadius: '6px',
              fontSize: '0.9rem',
              outline: 'none',
              textAlign: 'center',
              boxSizing: 'border-box'
            }}
            placeholder="Type DELETE to confirm"
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            autoFocus
          />
        </div>
        <div className="prod-bulk-actions" style={{ justifyContent: 'center' }}>
          <button className="prod-bulk-cancel" onClick={onClose}>Cancel</button>
          <button
            className="prod-bulk-apply"
            disabled={!isValid || deleting}
            onClick={handleDelete}
            style={{ background: '#dc2626', opacity: isValid ? 1 : 0.5 }}
          >
            {deleting ? 'Deleting...' : 'Delete Permanently'}
          </button>
        </div>
      </div>
    </div>
  );
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    const day = String(date.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return d;
  }
};

const calculateNoOfDays = (start, end) => {
  if (!start || !end) return '—';
  try {
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return '—';
    s.setHours(0, 0, 0, 0);
    e.setHours(0, 0, 0, 0);
    const diff = e.getTime() - s.getTime();
    const days = Math.round(diff / (1000 * 60 * 60 * 24));
    return days >= 0 ? days + 1 : '—';
  } catch {
    return '—';
  }
};

const ComplexityBadge = ({ value }) => {
  if (!value) return <span className="cell-dash">-</span>;
  const complexityKey = value.toLowerCase().replace(/\s+/g, '');
  return (
    <span className={`complexity-badge complexity-${complexityKey}`}>
      {value}
    </span>
  );
};

const Production = () => {
  // Lists
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [jobs, setJobs] = useState([]);

  // Loading & Error States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters State
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [filters, setFilters] = useState({
    clientId: '',
    projectId: '',
    workflowId: '',
    jobId: '',
    complexity: [],
    processStatus: [],
    qcStatus: [],
    startDate: '',
    endDate: '',
  });


  // Pagination
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  // Unsaved Edits State: { [jobId]: { processStatus, qcStatus, endDate } }
  const [edits, setEdits] = useState({});
  const [savingRows, setSavingRows] = useState({}); // { [jobId]: boolean }
  const [successRows, setSuccessRows] = useState({}); // { [jobId]: boolean }

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkDeleteModalState, setBulkDeleteModalState] = useState(null); // null | 'delete' | 'reconfirm'
  const hasActiveFilters = Object.values(filters).some(val => val !== '' && val !== null);

  const _user = getCurrentUser();
  const _roles = _user?.roles || [];
  const _perms = _user?.permissions || [];
  const isAdmin = _roles.includes('Admin');
  const canDelete = isAdmin || _perms.includes('jobs.delete');
  const topScrollRef = React.useRef(null);
  const bottomScrollRef = React.useRef(null);

  // ── Load dropdown reference data ─────────────────────────────────────
  const loadDropdowns = useCallback(async () => {
    try {
      const [proj, cl, wf] = await Promise.all([
        apiCall('/projects'),
        apiCall('/clients'),
        apiCall('/projects/workflows'),
      ]);
      setProjects(proj.map(p => ({
        id: p.id,
        name: p.name,
        clientId: p.clientId,
        clientName: p.clientName,
        workflowId: p.workflowId,
        workflowName: p.workflowName,
      })) || []);
      setClients(cl || []);
      setWorkflows(wf || []);
    } catch (err) {
      console.warn('Could not load dropdowns:', err.message);
    }
  }, []);

  // ── Load production jobs ──────────────────────────────────────────
  const loadProductionJobs = useCallback(async (pageNum = 0, size = pageSize) => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: pageNum,
        size: size,
        ...(filters.clientId && { clientId: filters.clientId }),
        ...(filters.projectId && { projectId: filters.projectId }),
        ...(filters.workflowId && { workflowId: filters.workflowId }),
        ...(filters.jobId && { jobIdCode: filters.jobId }),
        ...(filters.complexity.length > 0 && { complexity: filters.complexity.join(',') }),
        ...(filters.processStatus.length > 0 && { processStatus: filters.processStatus.join(',') }),
        ...(filters.qcStatus.length > 0 && { qcStatus: filters.qcStatus.join(',') }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
      });

      const data = await apiCall(`/jobs/production/search?${params}`);
      setJobs(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalItems(data.totalElements || 0);
      setPage(pageNum);
      // Clear edits and success markers when reload happens
      setEdits({});
      setSuccessRows({});
    } catch (err) {
      setError('Failed to load production details: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize]);

  // Debounced auto-search when filters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handler = setTimeout(() => {
      loadProductionJobs(0);
    }, 300);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pageSize]);

  useEffect(() => {
    loadDropdowns();
  }, [loadDropdowns]);

  useEffect(() => {
    const topEl = topScrollRef.current;
    const bottomEl = bottomScrollRef.current;
    if (!topEl || !bottomEl) return;
    const ro = new ResizeObserver(() => {
      const fc = bottomEl.firstElementChild;
      if (fc) {
        const id = topEl.firstElementChild;
        if (id) id.style.width = `${fc.offsetWidth}px`;
      }
    });
    ro.observe(bottomEl);
    let syncT = false, syncB = false;
    const onTop = () => { if (!syncB) { syncT = true; bottomEl.scrollLeft = topEl.scrollLeft; syncT = false; } };
    const onBottom = () => { if (!syncT) { syncB = true; topEl.scrollLeft = bottomEl.scrollLeft; syncB = false; } };
    topEl.addEventListener('scroll', onTop);
    bottomEl.addEventListener('scroll', onBottom);
    return () => {
      ro.disconnect();
      topEl.removeEventListener('scroll', onTop);
      bottomEl.removeEventListener('scroll', onBottom);
    };
  }, [jobs]);

  // ── Search & Reset Handlers ───────────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    loadProductionJobs(0);
  };

  const handleReset = () => {
    setFilters({
      clientId: '',
      projectId: '',
      workflowId: '',
      jobId: '',
      complexity: [],
      processStatus: [],
      qcStatus: [],
      startDate: '',
      endDate: '',
    });
  };

  // ── Edit Handlers ──────────────────────────────────────────────────
  const handleCellChange = (jobId, field, value) => {
    setEdits(prev => {
      const rowEdits = prev[jobId] || {};
      const job = jobs.find(j => j.id === jobId);

      const newEdits = {
        ...rowEdits,
        [field]: value
      };

      if (field === 'qcStatus' && value !== 'UPLOADED') {
        newEdits.endDate = '';
      }
      if (field === 'qcStatus' && value === 'UPLOADED') {
        newEdits.processStatus = 'FINISH';
      }

      // If the edited values match the original values, remove the edit key
      const currentProcessStatus = newEdits.hasOwnProperty('processStatus') ? newEdits.processStatus : (job.processStatus || 'PENDING');
      const currentQcStatus = newEdits.hasOwnProperty('qcStatus') ? newEdits.qcStatus : (job.qcStatus || 'PENDING');
      const currentEndDate = newEdits.hasOwnProperty('endDate') ? newEdits.endDate : (job.endDate || '');
      const currentEmployees = newEdits.hasOwnProperty('employees') ? newEdits.employees : (job.employees ? job.employees.join(', ') : '');
      const currentRefType = newEdits.hasOwnProperty('refType') ? newEdits.refType : (job.referenceType || '');
      const currentQcEmployees = newEdits.hasOwnProperty('qcEmployees') ? newEdits.qcEmployees : (job.qcEmployees ? job.qcEmployees.join(' | ') : '');

      const matchesOriginal =
        currentProcessStatus === (job.processStatus || 'PENDING') &&
        currentQcStatus === (job.qcStatus || 'PENDING') &&
        currentEndDate === (job.endDate || '') &&
        (newEdits.hasOwnProperty('startMonth') ? (newEdits.startMonth || null) === (job.startMonth || null) : true) &&
        currentEmployees === (job.employees ? job.employees.join(', ') : '') &&
        currentQcEmployees === (job.qcEmployees ? job.qcEmployees.join(' | ') : '') &&
        currentRefType === (job.referenceType || '');

      if (matchesOriginal) {
        const updated = { ...prev };
        delete updated[jobId];
        return updated;
      }

      return {
        ...prev,
        [jobId]: newEdits
      };
    });

    // Clear success checkmark on further change
    if (successRows[jobId]) {
      setSuccessRows(prev => ({ ...prev, [jobId]: false }));
    }
  };

  // ── Save Handler ───────────────────────────────────────────────────
  const handleSaveRow = async (jobId) => {
    const rowEdits = edits[jobId];
    if (!rowEdits) return;

    const job = jobs.find(j => j.id === jobId);
    const payload = {
      processStatus: rowEdits.hasOwnProperty('processStatus') ? rowEdits.processStatus : job.processStatus,
      qcStatus: rowEdits.hasOwnProperty('qcStatus') ? rowEdits.qcStatus : job.qcStatus,
      endDate: rowEdits.hasOwnProperty('endDate') ? rowEdits.endDate : job.endDate,
      refType: rowEdits.hasOwnProperty('refType') ? rowEdits.refType : (job.referenceType || ''),
      startMonth: rowEdits.hasOwnProperty('startMonth') ? rowEdits.startMonth : job.startMonth,
      employees: rowEdits.hasOwnProperty('employees')
        ? rowEdits.employees.split(',').map(s => s.trim()).filter(Boolean)
        : (job.employees || []),
      qcEmployees: rowEdits.hasOwnProperty('qcEmployees')
        ? rowEdits.qcEmployees.split(/[,|]/).map(s => s.trim()).filter(Boolean)
        : (job.qcEmployees || []),
    };

    setSavingRows(prev => ({ ...prev, [jobId]: true }));
    try {
      const updatedJob = await apiCall(`/jobs/${jobId}/production`, 'PUT', payload);

      // Update local jobs list with saved response
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, ...updatedJob } : j));

      // Remove edits for this row
      setEdits(prev => {
        const copy = { ...prev };
        delete copy[jobId];
        return copy;
      });

      // Show success checkmark
      setSuccessRows(prev => ({ ...prev, [jobId]: true }));
      // Fade out success checkmark after 3 seconds
      setTimeout(() => {
        setSuccessRows(prev => ({ ...prev, [jobId]: false }));
      }, 3000);

    } catch (err) {
      alert(`Failed to save production details: ${err.message}`);
    } finally {
      setSavingRows(prev => ({ ...prev, [jobId]: false }));
    }
  };

  const getProcessStatusClass = (status) => {
    if (!status) return 'prod-status-pill prod-sp-pending';
    return `prod-status-pill prod-sp-${status.toLowerCase()}`;
  };

  const getQcStatusClass = (status) => {
    if (!status) return 'qc-status-pill qc-sp-pending';
    return `qc-status-pill qc-sp-${status.toLowerCase()}`;
  };

  const getProjectBadgeClass = (projectName) => {
    if (!projectName) return 'proj-badge-default';
    let hash = 0;
    for (let i = 0; i < projectName.length; i++) {
      hash = projectName.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % 8;
    return `proj-badge proj-badge-${index}`;
  };

  // ── Bulk selection handlers ─────────────────────────────────────────
  const toggleRow = id => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAll = () => {
    if (selectedIds.size === jobs.length && jobs.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(jobs.map(j => j.id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkEditDone = async () => {
    clearSelection();
    await loadProductionJobs(page);
  };

  const handleBulkDelete = async (ids) => {
    await apiCall('/jobs/bulk-delete', 'DELETE', { ids });
    clearSelection();
    await loadProductionJobs(page);
  };

  const handleScreenshot = async () => {
    try {
      const { default: html2canvas } = await import('html2canvas');
      const wrapper = document.querySelector('.table-wrapper');

      if (wrapper) {
        const origOverflowX = wrapper.style.overflowX;
        const origWidth = wrapper.style.width;

        const origPosition = wrapper.style.position;
        const origTop = wrapper.style.top;
        const origLeft = wrapper.style.left;
        const origZIndex = wrapper.style.zIndex;

        // Force it out of the document flow so parents can't squeeze it
        wrapper.style.position = 'absolute';
        wrapper.style.top = '0';
        wrapper.style.left = '0';
        wrapper.style.zIndex = '9999';

        const fullWidth = wrapper.scrollWidth;
        wrapper.style.overflowX = 'visible';
        wrapper.style.width = `${fullWidth}px`;

        const canvas = await html2canvas(wrapper, {
          scale: 2,
          useCORS: true,
          windowWidth: fullWidth,
          scrollX: 0
        });

        wrapper.style.overflowX = origOverflowX;
        wrapper.style.width = origWidth;
        wrapper.style.position = origPosition;
        wrapper.style.top = origTop;
        wrapper.style.left = origLeft;
        wrapper.style.zIndex = origZIndex;

        const imgData = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = imgData;
        link.download = `Production_Screenshot_${new Date().toISOString().slice(0, 10)}.png`;
        link.click();
      }
    } catch (err) {
      console.error('Screenshot failed', err);
    }
  };

  return (
    <div className="production-container">
      <div className="bj-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="bj-page-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="bj-page-icon">
            <img src={productionIcon} alt="Production" className="bj-page-title-img-icon" />
          </span>
          <h2>Production Details</h2>
          <span
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            style={{
              cursor: 'pointer',
              fontSize: '1.2rem',
              color: '#64748b',
              marginLeft: '8px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              transition: 'background-color 0.2s',
              userSelect: 'none'
            }}
            onMouseOver={e => e.currentTarget.style.backgroundColor = '#f1f5f9'}
            onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
            title={isFilterOpen ? 'Close Filter' : 'Open Filter'}
          >
            {isFilterOpen ? '▲' : '▼'}
          </span>
        </div>

        <div className="bj-header-btns" style={{ display: 'flex', gap: '10px' }}>
          <button className="bj-screenshot-btn" onClick={handleScreenshot}>
            📸 Screenshot
          </button>
        </div>
      </div>

      {/* Filter panel */}
      {isFilterOpen && (
        <div className="production-filter-card">
          <form onSubmit={handleSearch}>
            <div className="filter-grid">
              {/* Client Select */}
              <div className="filter-group">
                <label htmlFor="clientId">💼 Client</label>
                <select
                  id="clientId"
                  value={filters.clientId}
                  onChange={e => setFilters(prev => ({
                    ...prev,
                    clientId: e.target.value,
                    projectId: '',
                    workflowId: '',
                  }))}
                >
                  <option value="">All Client</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.companyName}</option>
                  ))}
                </select>
              </div>

              {/* Project Select */}
              <div className="filter-group">
                <label htmlFor="projectId">📦 Project</label>
                <select
                  id="projectId"
                  value={filters.projectId}
                  onChange={e => {
                    const proj = projects.find(p => p.id === e.target.value);
                    setFilters(prev => ({
                      ...prev,
                      projectId: e.target.value,
                      clientId: proj && proj.clientId ? proj.clientId : prev.clientId,
                      workflowId: proj && proj.workflowId ? proj.workflowId : prev.workflowId,
                    }));
                  }}
                >
                  <option value="">All Projects</option>
                  {(filters.clientId
                    ? projects.filter(p => p.clientId === filters.clientId)
                    : projects
                  ).map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* Workflow Select */}
              <div className="filter-group">
                <label htmlFor="workflowId">⚙️ Task Name</label>
                <select
                  id="workflowId"
                  value={filters.workflowId}
                  onChange={e => setFilters(prev => ({ ...prev, workflowId: e.target.value }))}
                >
                  <option value="">All Task Names</option>
                  {workflows.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </div>

              {/* Job ID Input */}
              <div className="filter-group">
                <label htmlFor="jobId">🆔 Job ID</label>
                <input
                  type="text"
                  id="jobId"
                  placeholder="e.g. BM0748"
                  value={filters.jobId}
                  onChange={e => setFilters(prev => ({ ...prev, jobId: e.target.value }))}
                />
              </div>

              {/* Complexity Select */}
              <MultiSelectDropdown
                label="⚡ Complexity"
                options={['Simple', 'Medium', 'Complex', 'Heavy Complex']}
                value={filters.complexity}
                onChange={val => setFilters(prev => ({ ...prev, complexity: val }))}
                placeholder="All Complexities"
              />

              {/* Process Status Select */}
              <MultiSelectDropdown
                label="⚙️ Process Status"
                options={STATUS_OPTIONS}
                value={filters.processStatus}
                onChange={val => setFilters(prev => ({ ...prev, processStatus: val }))}
                placeholder="All Process Status"
              />

              {/* QC Status Select */}
              <MultiSelectDropdown
                label="🧪 QC Status"
                options={QC_STATUS_OPTIONS}
                value={filters.qcStatus}
                onChange={val => setFilters(prev => ({ ...prev, qcStatus: val }))}
                placeholder="All QC Status"
              />

              {/* Start Date */}
              <div className="filter-group">
                <label htmlFor="startDate">Start Date (From)</label>
                <input
                  type="date"
                  id="startDate"
                  value={filters.startDate}
                  onChange={e => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>

              {/* End Date */}
              <div className="filter-group">
                <label htmlFor="endDate">End Date (To)</label>
                <input
                  type="date"
                  id="endDate"
                  value={filters.endDate}
                  onChange={e => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>

              <div className="filter-actions-group">
                <div className="filter-results-left">
                  {hasActiveFilters && (
                    <span className="filter-total-pages" style={{ fontWeight: '700', color: '#475569', fontSize: '0.85rem', background: '#f8fafc', padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                      Total Filtered Pages: {jobs.reduce((sum, j) => sum + (parseInt(j.pageCount) || 0), 0)}
                    </span>
                  )}
                </div>
                <div className="filter-buttons-right">
                  <button type="button" onClick={handleReset} className="btn-reset">
                    🔄 Reset
                  </button>
                  <button type="submit" className="btn-search">
                    🔍 Search
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
      {/* Pagination header */}
      <div className="table-pagination-footer" style={{ borderBottom: '1px solid #e2e8f0', borderTop: 'none', padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>Items per page:</label>
          <select
            value={pageSize}
            onChange={e => {
              const newSize = Number(e.target.value);
              setPageSize(newSize);
              loadProductionJobs(0, newSize);
            }}
            style={{
              padding: '4px 8px',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              outline: 'none',
              fontSize: '0.8rem',
              backgroundColor: '#ffffff',
              color: '#334155'
            }}
          >
            {[10, 25, 50, 100].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <span className="pagination-info">
          Showing page <strong>{page + 1}</strong> of <strong>{totalPages || 1}</strong> ({totalItems} items)
        </span>
        <div className="pagination-controls">
          <button
            disabled={page === 0 || loading}
            onClick={() => loadProductionJobs(page - 1)}
            className="btn-page"
          >
            ◀ Previous
          </button>
          <button
            disabled={page >= totalPages - 1 || loading}
            onClick={() => loadProductionJobs(page + 1)}
            className="btn-page"
          >
            Next ▶
          </button>
        </div>
      </div>

      {/* Grid view */}
      <div className="production-table-card">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading production records...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p className="error-text">⚠️ {error}</p>
            <button onClick={() => loadProductionJobs(page)} className="btn-retry">
              Try Again
            </button>
          </div>
        ) : jobs.length === 0 ? (
          <div className="empty-state">
            <span style={{ fontSize: '3rem' }}>📂</span>
            <h3>No Production Records Found</h3>
            <p>Modify search filters or assign tasks to jobs to start tracking.</p>
          </div>
        ) : (
          <>
            {/* Bulk toolbar */}
            {selectedIds.size > 0 && (
              <div className="prod-table-topbar">
                <div className="prod-bulk-toolbar">
                  <span className="prod-bulk-count">✅ {selectedIds.size} selected</span>
                  <button className="prod-bulk-edit-btn" onClick={() => setShowBulkEdit(true)}>
                    ✏️ Bulk Edit
                  </button>
                  {canDelete && (
                    <button
                      className="prod-bulk-delete-btn"
                      onClick={() => setBulkDeleteModalState('delete')}
                      style={{
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        padding: '6px 16px',
                        borderRadius: '6px',
                        fontSize: '0.82rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                        whiteSpace: 'nowrap',
                      }}
                      onMouseEnter={e => e.target.style.background = '#dc2626'}
                      onMouseLeave={e => e.target.style.background = '#ef4444'}
                    >
                      🗑️ Bulk Delete
                    </button>
                  )}
                  <button className="prod-bulk-deselect-btn" onClick={clearSelection}>
                    ✕ Deselect All
                  </button>
                </div>
              </div>
            )}

            <div className="double-scroll-top" ref={topScrollRef}>
              <div className="double-scroll-top-inner" />
            </div>
            <div className="table-wrapper" ref={bottomScrollRef} style={{ flex: 1, overflowY: 'auto' }}>
              <table className="production-table">
                <thead>
                  <tr>
                    <th className="prod-th-check">
                      <input
                        type="checkbox"
                        className="prod-row-checkbox"
                        checked={jobs.length > 0 && selectedIds.size === jobs.length}
                        onChange={toggleAll}
                        title="Select all on this page"
                      />
                    </th>
                    <th>Client</th>
                    <th>Project</th>
                    <th>Task Name</th>
                    <th>Receive Date</th>
                    <th>Job ID</th>
                    <th>ISBN</th>
                    <th style={{ minWidth: '120px' }}>Batch</th>
                    <th style={{ minWidth: '150px' }}>Title Name</th>
                    <th>Page</th>
                    <th>PDF Type</th>
                    <th>Complexity</th>
                    <th style={{ minWidth: '130px' }}>Ref Type</th>
                    <th style={{ minWidth: '180px' }}>Employees Assigned</th>
                    <th style={{ minWidth: '130px' }}>Commenced Date</th>
                    <th style={{ minWidth: '130px' }}>Process Status</th>
                    <th style={{ minWidth: '180px' }}>QC / Valid Employee</th>
                    <th style={{ minWidth: '150px' }}>QC Status</th>
                    <th>End Date</th>
                    <th>No. Day</th>
                    <th className="actions-col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => {
                    const rowEdits = edits[job.id] || {};
                    const isSaving = savingRows[job.id] || false;
                    const isSuccess = successRows[job.id] || false;
                    const isModified = edits.hasOwnProperty(job.id);

                    const currentProcessStatus = rowEdits.hasOwnProperty('processStatus')
                      ? rowEdits.processStatus
                      : (job.processStatus || 'PENDING');
                    const currentQcStatus = rowEdits.hasOwnProperty('qcStatus')
                      ? rowEdits.qcStatus
                      : (job.qcStatus || 'PENDING');
                    const currentEndDate = rowEdits.hasOwnProperty('endDate')
                      ? rowEdits.endDate
                      : (job.endDate || '');
                    const currentStartMonth = rowEdits.hasOwnProperty('startMonth')
                      ? (rowEdits.startMonth || (job.startMonth || job.productionStartDate || ''))
                      : (job.startMonth || job.productionStartDate || '');
                    const isAutoStartMonth = rowEdits.hasOwnProperty('startMonth')
                      ? (!rowEdits.startMonth && !!job.productionStartDate)
                      : (!job.startMonth && !!job.productionStartDate);
                    const currentEmployees = rowEdits.hasOwnProperty('employees')
                      ? rowEdits.employees
                      : (job.employees ? job.employees.join(' | ') : '');

                    const currentQcEmployees = rowEdits.hasOwnProperty('qcEmployees')
                      ? rowEdits.qcEmployees
                      : (job.qcEmployees ? job.qcEmployees.join(' | ') : '');

                    return (
                      <tr key={job.id} className={`${isModified ? 'modified-row' : ''} ${selectedIds.has(job.id) ? 'prod-row-selected' : ''}`}>
                        <td className="prod-td-check">
                          <input
                            type="checkbox"
                            className="prod-row-checkbox"
                            checked={selectedIds.has(job.id)}
                            onChange={() => toggleRow(job.id)}
                          />
                        </td>
                        <td className="client-name-col" title={job.clientName}>
                          {job.clientName ? (
                            <span className="client-badge" style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', padding: '2px 6px', borderRadius: '4px', fontSize: '0.78rem', fontWeight: 700 }}>
                              {job.clientName}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="proj-name-col" title={job.projectName}>
                          {job.projectName ? (
                            <span className={getProjectBadgeClass(job.projectName)}>
                              {job.projectName}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="workflow-name-col" title={job.workflowName}>
                          {job.workflowName ? (
                            <span className="badge badge--workflow" style={{ background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', padding: '2px 6px', borderRadius: '4px', fontSize: '0.78rem', fontWeight: 700 }}>
                              {job.workflowName}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="date-col">
                          {fmtDate(job.receiveDate)}
                        </td>
                        <td className="job-code-col">
                          <strong>{job.jobIdCode}</strong>
                        </td>
                        <td className="isbn-col">
                          {job.xmlIsbn || '—'}
                        </td>
                        <td className="batch-col">
                          {job.batch || '—'}
                        </td>
                        <td className="title-col" title={job.titleName}>
                          <div className="title-text-trunc">
                            {job.titleName}
                          </div>
                        </td>
                        <td className="page-count-col">
                          {job.pageCount !== undefined && job.pageCount !== null ? job.pageCount : '—'}
                        </td>
                        <td className="pdf-type-col">
                          {job.pdfInputType || '—'}
                        </td>
                        <td className="complexity-col">
                          <ComplexityBadge value={job.complexity} />
                        </td>
                        <td className="reftype-col">
                          <select
                            className="inline-select"
                            value={rowEdits.hasOwnProperty('refType') ? rowEdits.refType : (job.referenceType || '')}
                            onChange={e => handleCellChange(job.id, 'refType', e.target.value)}
                            disabled={isSaving}
                          >
                            <option value="">-</option>
                            {REF_TYPES.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </td>
                        <td className="employees-col">
                          <div className={`employee-input-wrapper${isSaving ? ' is-disabled' : ''}`}>
                            <input
                              type="text"
                              className="inline-employee-input"
                              value={currentEmployees}
                              onChange={e => handleCellChange(job.id, 'employees', e.target.value)}
                              disabled={isSaving}
                              placeholder="Enter employee names..."
                            />
                          </div>
                        </td>
                        <td>
                          <input
                            type="date"
                            className={`inline-date-input ${isAutoStartMonth ? 'auto-date' : 'manual-date'}`}
                            value={currentStartMonth}
                            onChange={e => handleCellChange(job.id, 'startMonth', e.target.value)}
                            disabled={isSaving}
                            title={isAutoStartMonth ? "Automatically set from employee's first task. Click to override manually." : "Manually entered start month."}
                          />
                        </td>
                        <td>
                          <select
                            className={`inline-select ${getProcessStatusClass(currentProcessStatus)}`}
                            value={currentProcessStatus}
                            onChange={e => handleCellChange(job.id, 'processStatus', e.target.value)}
                            disabled={isSaving}
                          >
                            {STATUS_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </td>
                        <td className="employees-col">
                          <div className={`employee-input-wrapper${isSaving ? ' is-disabled' : ''}`}>
                            <input
                              type="text"
                              className="inline-employee-input"
                              value={currentQcEmployees}
                              onChange={e => handleCellChange(job.id, 'qcEmployees', e.target.value)}
                              disabled={isSaving}
                              placeholder="QC employee names..."
                            />
                          </div>
                        </td>
                        <td>
                          <select
                            className={`inline-select ${getQcStatusClass(currentQcStatus)}`}
                            value={currentQcStatus}
                            onChange={e => handleCellChange(job.id, 'qcStatus', e.target.value)}
                            disabled={isSaving}
                          >
                            {QC_STATUS_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="date"
                            className="inline-date-input"
                            value={currentEndDate}
                            onChange={e => handleCellChange(job.id, 'endDate', e.target.value)}
                            disabled={isSaving || currentQcStatus !== 'UPLOADED'}
                          />
                        </td>
                        <td style={{ fontWeight: 'bold', color: '#475569' }}>
                          {calculateNoOfDays(currentStartMonth, currentEndDate)}
                        </td>
                        <td className="actions-col">
                          {isSaving ? (
                            <span className="row-spinner" />
                          ) : isSuccess ? (
                            <span className="save-success-icon" title="Saved Successfully">✅</span>
                          ) : (
                            <button
                              className="btn-save-row"
                              disabled={!isModified}
                              onClick={() => handleSaveRow(job.id)}
                              title="Save Changes"
                            >
                              💾 Save
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

          </>
        )}
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEdit && (
        <BulkEditProductionModal
          selectedIds={Array.from(selectedIds)}
          selectedCount={selectedIds.size}
          jobs={jobs}
          onClose={() => setShowBulkEdit(false)}
          onDone={handleBulkEditDone}
        />
      )}

      {bulkDeleteModalState === 'delete' && (
        <BulkDeleteProductionModal
          selectedCount={selectedIds.size}
          onClose={() => setBulkDeleteModalState(null)}
          onDelete={() => setBulkDeleteModalState('reconfirm')}
        />
      )}

      {bulkDeleteModalState === 'reconfirm' && (
        <BulkReconfirmDeleteProductionModal
          selectedIds={Array.from(selectedIds)}
          selectedCount={selectedIds.size}
          onClose={() => setBulkDeleteModalState(null)}
          onDelete={handleBulkDelete}
        />
      )}
    </div>
  );
};

export default Production;
